import { DeviceOperationalState } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { GET_UPDATE_DEVICE_OPERATIONAL_STATE } from '@features/deviceConfigurationModal/graphql/queries/getUpdateDeviceStatusFields';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { DeviceTabProps } from '../components/tabProps';
import UPDATE_DEVICE_OPERATIONAL_STATE from '../graphql/mutations/updateDeviceOperationalState';

const schema = z.object({
  deviceId: z.string(),
  operationalState: z.nativeEnum(DeviceOperationalState),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  deviceId: string;
  onUpdateCompleted?: (
    previousState: DeviceOperationalState,
    newState: DeviceOperationalState,
  ) => void;
  setIsDirty?: DeviceTabProps['setIsDirty'];
}

export const useUpdateDeviceOperationalStateForm = (props: Props) => {
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const { data } = useSuspenseQuery(GET_UPDATE_DEVICE_OPERATIONAL_STATE, {
    variables: { deviceId: props.deviceId },
  });
  const { _ } = useLingui();
  const [update] = useMutation(UPDATE_DEVICE_OPERATIONAL_STATE, {
    onCompleted({ updateDevice }) {
      openModalSuccessMessage(_(msg`Dispositivo actualizado correctamente`));
      methods.reset({
        deviceId: updateDevice.profile.id,
        operationalState: updateDevice.profile.operationalState,
      });
    },
    onError(error) {
      openModalErrorMessage(_(msg`Error al actualizar el dispositivo: ${error.message}`));
    },
    update(cache) {
      cache.modify({
        id: cache.identify({ __typename: 'Device', id: props.deviceId }),
        fields: {
          profile(_, { INVALIDATE }) {
            return INVALIDATE;
          },
        },
      });
    },
    onQueryUpdated(observableQuery) {
      return observableQuery.refetch();
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      deviceId: props.deviceId,
      operationalState: data.device.profile.operationalState,
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = methods.handleSubmit(async (formData) => {
    const result = await update({
      variables: {
        deviceId: formData.deviceId,
        operationalState: formData.operationalState,
      },
    });
    if (result.data) {
      const previousState = data.device.profile.operationalState;
      const newState = result.data.updateDevice.profile.operationalState;
      props.onUpdateCompleted?.(previousState, newState);
    }
  });

  return {
    currentOperationalState: data.device.profile.operationalState,
    onSubmit,
    methods,
  };
};
